import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  ArrowTopRightOnSquareIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { isValidSiret, normalizeSiret, SIRET_ERROR } from '../utils/siret';

export default function ModifierUNAF() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const signatureRef = useRef(null);
  
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1); // 1: Sélection, 2: Signature, 3: Paiement
  
  const [modifications, setModifications] = useState({
    assuranceFormule: null,
    revue: null,
    affairesJuridiques: false,
    ecocontribution: false,
  });
  const [signature, setSignature] = useState('');
  const [montantSupplementaire, setMontantSupplementaire] = useState(0);
  const [siret, setSiret] = useState('');
  const [napi, setNapi] = useState('');

  // Tarifs
  const TARIFS = {
    affairesJuridiques: 0.15,
    ecocontribution: 0.12,
    revue: { papier: 31, numerique: 18, papier_numerique: 35, aucun: 0 },
    assurance: { formule1: 0.10, formule2: 1.65, formule3: 2.80 },
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchService();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, id, navigate]);

  const fetchService = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/services/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.typeService !== 'assurance_unaf') {
        setError('Ce service ne peut pas être modifié');
        return;
      }

      // Vérifier s'il y a une modification en attente de validation (payée mais non validée)
      const pendingValidation = response.data.historiqueModifications?.find(
        (m) => m.paiement?.status === 'paye' && !m.validated
      );
      if (pendingValidation) {
        setError('Une modification est en attente de validation. Vous ne pouvez pas effectuer de nouvelle modification tant que la précédente n\'a pas été validée.');
        return;
      }
      
      setService(response.data);
      // Pré-remplir SIRET et NAPI depuis le service ou le profil user
      setSiret(response.data.unafData?.siret || user?.siret || '');
      setNapi(response.data.unafData?.napi || user?.napi || '');
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du chargement du service');
    } finally {
      setLoading(false);
    }
  };

  const currentOptions = service?.unafData?.options || {};
  const nombreRuches = service?.unafData?.nombreRuches || 0;

  // Vérifier si une option peut être modifiée
  const canAddFormule = () => {
    // On peut ajouter une formule seulement si aucune formule n'est actuellement souscrite
    return !currentOptions.assurance?.formule || currentOptions.assurance?.formule === 'aucune';
  };

  const canAddRevue = () => {
    return !currentOptions.revue?.choix || currentOptions.revue?.choix === 'aucun';
  };

  const canAddAffairesJuridiques = () => {
    return !currentOptions.affairesJuridiques?.souscrit;
  };

  const canAddEcocontribution = () => {
    return !currentOptions.ecocontribution?.souscrit;
  };

  // Gérer les dépendances SIRET/NAPI
  useEffect(() => {
    // Si SIRET ou NAPI sont enlevés, décocher écocontribution
    if ((!siret?.trim() || !napi?.trim()) && modifications.ecocontribution) {
      setModifications(prev => ({ ...prev, ecocontribution: false }));
      alert('⚠️ L\'écocontribution a été automatiquement décochée car le SIRET et le NAPI sont requis pour cette option.');
    }
    
    // Si NAPI est enlevé, remettre la formule d'assurance à null
    if (!napi?.trim() && modifications.assuranceFormule) {
      setModifications(prev => ({ ...prev, assuranceFormule: null }));
      alert('⚠️ La formule d\'assurance a été automatiquement désélectionnée car le NAPI est requis pour cette option.');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siret, napi]);

  // Calculer le montant supplémentaire
  useEffect(() => {
    if (!service) return;
    
    let supplement = 0;
    
    // Ajout formule assurance (seulement si aucune formule actuellement)
    if (modifications.assuranceFormule && canAddFormule()) {
      const nouveauPrix = TARIFS.assurance[modifications.assuranceFormule] || 0;
      supplement += nouveauPrix * nombreRuches;
    }
    
    // Ajout revue
    if (modifications.revue && modifications.revue !== 'aucun' && canAddRevue()) {
      supplement += TARIFS.revue[modifications.revue] || 0;
    }
    
    // Ajout affaires juridiques
    if (modifications.affairesJuridiques && canAddAffairesJuridiques()) {
      supplement += TARIFS.affairesJuridiques * nombreRuches;
    }
    
    // Ajout écocontribution
    if (modifications.ecocontribution && canAddEcocontribution()) {
      supplement += TARIFS.ecocontribution * nombreRuches;
    }
    
    setMontantSupplementaire(Math.round(supplement * 100) / 100);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modifications, service]);

  const handleSignature = () => {
    if (signatureRef.current) {
      const signatureData = signatureRef.current.toDataURL();
      setSignature(signatureData);
    }
  };

  const clearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
      setSignature('');
    }
  };

  const handleSubmit = async () => {
    if (montantSupplementaire <= 0) {
      setError('Aucune modification sélectionnée');
      return;
    }
    
    if (montantSupplementaire < 0.50) {
      setError('Attention, vous ne pouvez pas souscrire à des services supplémentaires en dessous d\'un montant total cumulé de 0,50€ (le montant minimum de perception permis par la plateforme).');
      return;
    }
    
    // Vérifier que le SIRET est renseigné si écocontribution est cochée
    if (modifications.ecocontribution && !siret?.trim()) {
      setError('Le SIRET est requis pour souscrire à l\'écocontribution');
      return;
    }
    if (!isValidSiret(siret, { required: Boolean(modifications.ecocontribution) })) {
      setError(SIRET_ERROR);
      return;
    }
    
    if (!signature) {
      setError('Veuillez signer pour valider les modifications');
      return;
    }
    
    try {
      setSubmitting(true);
      setError('');
      const token = localStorage.getItem('token');
      
      // Enregistrer la modification
      const modificationsToSend = {
        ...modifications,
        siret: siret,
        napi: napi,
      };
      
      const modifyResponse = await axios.put(
        `${process.env.REACT_APP_API_URL}/services/${id}/modify-unaf`,
        { modifications: modificationsToSend, signature },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Créer la session de paiement
      const paymentResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/payment/service-modification/${id}/create-session`,
        { historiqueEntryIndex: modifyResponse.data.historiqueEntryIndex },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Rediriger vers Stripe
      if (paymentResponse.data.url) {
        window.location.href = paymentResponse.data.url;
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la modification');
      setSubmitting(false);
    }
  };

  const formatMontant = (montant) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(montant);
  };

  const getFormuleLabel = (formule) => {
    const labels = {
      aucune: 'Aucune',
      formule1: 'Formule 1 (0,10€/ruche)',
      formule2: 'Formule 2 (1,65€/ruche)',
      formule3: 'Formule 3 (2,80€/ruche)',
    };
    return labels[formule] || formule;
  };

  const getRevueLabel = (revue) => {
    const labels = {
      papier: 'Papier (31€)',
      numerique: 'Numérique (18€)',
      papier_numerique: 'Papier + Numérique (35€)',
      aucun: 'Aucune',
    };
    return labels[revue] || revue;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-honey-500"></div>
      </div>
    );
  }

  if (error && !service) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-soft text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Erreur</h2>
          <p className="text-slate-600 mb-4">{error}</p>
          <Link
            to={`/service/${id}`}
            className="inline-flex items-center px-4 py-2 bg-honey-500 text-white rounded-lg hover:bg-honey-600"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Retour
          </Link>
        </div>
      </div>
    );
  }

  if (!service) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <Link
            to={`/service/${id}`}
            className="inline-flex items-center text-indigo-600 hover:text-indigo-700 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Retour à ma souscription
          </Link>
          
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900">
              Modifier ma souscription UNAF
            </h1>
            <p className="mt-2 text-lg text-gray-600">
              Année {service.annee} - {nombreRuches} ruches déclarées
            </p>
            <p className="mt-1 text-sm text-gray-500">
              Service proposé par le Syndicat Apicole de La Réunion (SAR), adhérent de l'UNAF
            </p>
          </div>
        </div>

        {/* Indicateur d'étapes */}
        <div className="mb-8">
          <div className="flex items-center justify-center space-x-4">
            {[1, 2].map((s) => (
              <div key={s} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step >= s
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {s}
                </div>
                {s < 2 && (
                  <div
                    className={`w-16 h-1 mx-2 ${
                      step > s ? 'bg-indigo-600' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-2 space-x-16 text-sm text-gray-600">
            <span>Sélection</span>
            <span>Signature & Paiement</span>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Étape 1: Sélection des modifications */}
        {step === 1 && (
          <div className="space-y-6">
            {/* Souscription actuelle */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Votre souscription actuelle</h2>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Formule assurance</span>
                    <span className="font-medium text-gray-900">{getFormuleLabel(currentOptions.assurance?.formule)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Revue "Abeilles & Fleurs"</span>
                    <span className="font-medium text-gray-900">{getRevueLabel(currentOptions.revue?.choix)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cotisation Affaires juridiques</span>
                    <span className={`font-medium ${currentOptions.affairesJuridiques?.souscrit ? 'text-green-600' : 'text-gray-400'}`}>
                      {currentOptions.affairesJuridiques?.souscrit ? 'Souscrit' : 'Non souscrit'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Écocontribution</span>
                    <span className={`font-medium ${currentOptions.ecocontribution?.souscrit ? 'text-green-600' : 'text-gray-400'}`}>
                      {currentOptions.ecocontribution?.souscrit ? 'Souscrit' : 'Non souscrit'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Modifications possibles */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Modifications disponibles</h2>
              
              {/* Avertissement nombre de ruches */}
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg mb-6">
                <div className="flex">
                  <InformationCircleIcon className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <p className="ml-3 text-sm text-blue-700">
                    <strong>Attention :</strong> Le nombre de ruches pris en compte est le même pour tous les services souscrits.
                  </p>
                </div>
              </div>

              {/* Informations apicoles - SIRET et NAPI */}
              <div className="bg-blue-50 p-4 rounded-lg mb-6 border border-blue-200">
                <h3 className="font-medium text-gray-900 mb-3">
                  Informations apicoles
                  <span className="text-sm text-gray-500 font-normal ml-2">(requis pour l'assurance et l'écocontribution)</span>
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      SIRET
                    </label>
                    <input
                      type="text"
                      value={siret}
                      maxLength="14"
                      onChange={(e) => setSiret(normalizeSiret(e.target.value))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="14 chiffres exactement"
                    />
                    {siret && siret.length !== 14 && (
                      <p className="text-xs text-red-500 mt-1">{SIRET_ERROR}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      NAPI
                    </label>
                    <input
                      type="text"
                      value={napi}
                      onChange={(e) => setNapi(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Numéro NAPI"
                    />
                  </div>
                </div>
              </div>

              {/* Ajouter formule assurance */}
              {canAddFormule() ? (
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-gray-900">Ajouter une formule d'assurance <span className="text-sm text-gray-500 font-normal">(facultative)</span></h3>
                  <a
                    href="https://www.unaf-apiculture.info/nos-services/assurances-et-declaration-de-sinistres/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:text-indigo-700 text-sm flex items-center"
                  >
                    Détails des formules
                    <ArrowTopRightOnSquareIcon className="h-4 w-4 ml-1" />
                  </a>
                </div>

                {/* Avertissement continuité d'assurance */}
                <div className="bg-honey-50 border-l-4 border-honey-400 p-4 rounded-r-lg mb-4">
                  <div className="ml-2">
                    <h3 className="text-sm font-bold text-honey-800 mb-2">
                      ⚠️ Important - Continuité d'assurance
                    </h3>
                    <p className="text-sm text-honey-700">
                      <strong>Attention :</strong> La continuité d'assurance des ruches au 1er janvier pour les adhérents assurés l'année précédente n'est possible que si le paiement intervient <strong>au plus tard le 18 janvier</strong>.
                    </p>
                    <p className="text-sm text-honey-700 mt-2">
                      Au-delà de cette date, la période d'assurance ne commencera qu'à la date de réception du paiement par l'UNAF.
                    </p>
                  </div>
                </div>

                {/* Avertissement modification de formule */}
                <div className="bg-red-50 border-l-4 border-red-400 p-3 rounded-r-lg mb-4">
                  <p className="text-sm text-red-700">
                    <strong>Attention :</strong> Aucune modification de formule n'est acceptée en cours d'année.
                  </p>
                </div>

                <div className="space-y-2">
                  {['formule1', 'formule2', 'formule3'].map((formule) => {
                    const isSelected = modifications.assuranceFormule === formule;
                    const isDisabled = !napi?.trim();
                    
                    return (
                      <label
                        key={formule}
                        className={`flex items-start p-3 bg-white rounded-md border ${
                          !isDisabled ? 'cursor-pointer hover:border-indigo-400' : 'cursor-not-allowed opacity-60'
                        }`}
                      >
                        <input
                          type="radio"
                          name="formule"
                          value={formule}
                          checked={isSelected}
                          disabled={isDisabled}
                          onChange={(e) => setModifications({ ...modifications, assuranceFormule: e.target.value })}
                          className="mt-1 h-4 w-4 text-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                        <div className="ml-3 flex-1">
                          <div className="flex justify-between">
                            <span className="font-medium text-gray-900">{getFormuleLabel(formule)}</span>
                            <span className="text-indigo-600 font-medium">
                              {formatMontant(TARIFS.assurance[formule])}/ruche
                            </span>
                          </div>
                          {napi?.trim() ? (
                            <p className="text-xs text-gray-400 mt-1">
                              Total: {formatMontant(TARIFS.assurance[formule] * nombreRuches)} ({nombreRuches} ruches)
                            </p>
                          ) : (
                            <p className="text-sm text-honey-600 mt-1">
                              ⚠️ Complétez votre NAPI pour activer cette option
                            </p>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
              ) : (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-300">
                  <div className="flex items-center text-gray-700">
                    <LockClosedIcon className="h-5 w-5 mr-2 text-gray-500" />
                    <div>
                      <span className="font-medium">Assurance déjà souscrite</span>
                      <p className="text-sm text-gray-600 mt-1">Formule actuelle : {getFormuleLabel(currentOptions.assurance?.formule)}</p>
                      <p className="text-sm text-red-600 mt-1">Aucune modification de formule n'est acceptée en cours d'année.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Ajouter revue */}
              {canAddRevue() ? (
                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-gray-900">Ajouter la revue "Abeilles & Fleurs" <span className="text-sm text-gray-500 font-normal">(facultative)</span></h3>
                    <a
                      href="https://www.unaf-apiculture.info/nos-services/revue-abeilles-fleurs/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 hover:text-indigo-700 text-sm flex items-center"
                    >
                      En savoir plus
                      <ArrowTopRightOnSquareIcon className="h-4 w-4 ml-1" />
                    </a>
                  </div>

                  {/* Avertissement modification */}
                  <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-r-lg mb-4">
                    <h3 className="font-semibold text-sm text-red-700 mb-2">Règles importantes</h3>
                    <ul className="space-y-1 text-sm text-red-700">
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>Aucune souscription ne peut être annulée.</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>La souscription peut intervenir en cours d'année.</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>Aucun changement de formule n'est possible en cours d'année.</span>
                      </li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    {['papier', 'numerique', 'papier_numerique'].map((revue) => (
                      <label
                        key={revue}
                        className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                          modifications.revue === revue
                            ? 'bg-indigo-50 border-indigo-300'
                            : 'bg-white border-gray-200 hover:border-indigo-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="revue"
                          value={revue}
                          checked={modifications.revue === revue}
                          onChange={(e) => setModifications({ ...modifications, revue: e.target.value })}
                          className="h-4 w-4 text-indigo-600"
                        />
                        <span className="ml-3">{getRevueLabel(revue)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-300">
                  <div className="flex items-center text-gray-700">
                    <LockClosedIcon className="h-5 w-5 mr-2 text-gray-500" />
                    <div>
                      <span className="font-medium">Revue déjà souscrite</span>
                      <p className="text-sm text-gray-600 mt-1">Formule actuelle : {getRevueLabel(currentOptions.revue?.choix)}</p>
                      <p className="text-sm text-red-600 mt-1">Aucune modification de formule n'est acceptée en cours d'année.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Ajouter affaires juridiques */}
              {canAddAffairesJuridiques() ? (
                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                  <h3 className="font-medium text-gray-900 mb-3">Cotisation Affaires Juridiques <span className="text-sm text-gray-500 font-normal">(facultative)</span></h3>
                  
                  {/* Avertissement */}
                  <div className="bg-red-50 border-l-4 border-red-400 p-3 rounded-r-lg mb-4">
                    <p className="text-sm text-red-700">
                      <strong>Attention :</strong> Aucune souscription ne peut être annulée, la souscription peut intervenir en cours d'année.
                    </p>
                  </div>
                  
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={modifications.affairesJuridiques}
                      onChange={(e) => setModifications({ ...modifications, affairesJuridiques: e.target.checked })}
                      className="h-4 w-4 text-indigo-600 rounded"
                    />
                    <div className="ml-3">
                      <span className="font-medium text-gray-900">Ajouter Cotisation Affaires Juridiques</span>
                      <span className="ml-2 text-indigo-600">{formatMontant(TARIFS.affairesJuridiques)}/ruche</span>
                      <p className="text-sm text-gray-500">Total: {formatMontant(TARIFS.affairesJuridiques * nombreRuches)}</p>
                    </div>
                  </label>
                </div>
              ) : (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-300">
                  <div className="flex items-center text-gray-700">
                    <CheckCircleIcon className="h-5 w-5 mr-2 text-green-500" />
                    <span className="font-medium">Affaires juridiques déjà souscrites</span>
                  </div>
                </div>
              )}

              {/* Ajouter écocontribution */}
              {canAddEcocontribution() ? (
                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                  <h3 className="font-medium text-gray-900 mb-3">Écocontribution <span className="text-sm text-gray-500 font-normal">(facultative)</span></h3>
                  
                  {/* Avertissement */}
                  <div className="bg-red-50 border-l-4 border-red-400 p-3 rounded-r-lg mb-4">
                    <p className="text-sm text-red-700">
                      <strong>Attention :</strong> Aucune souscription ne peut être annulée, la souscription peut intervenir en cours d'année.
                    </p>
                  </div>
                  
                  <div className="flex items-center justify-between mb-3">
                    <label className={`flex items-center ${(siret?.trim() && napi?.trim()) ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
                      <input
                        type="checkbox"
                        checked={modifications.ecocontribution}
                        onChange={(e) => setModifications({ ...modifications, ecocontribution: e.target.checked })}
                        disabled={!(siret?.trim() && napi?.trim())}
                        className="h-4 w-4 text-indigo-600 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <div className="ml-3">
                        <span className="font-medium text-gray-900">Souscrire à l'écocontribution</span>
                        <span className="ml-2 text-indigo-600">{formatMontant(TARIFS.ecocontribution)}/ruche</span>
                        {(siret?.trim() && napi?.trim()) ? (
                          <p className="text-sm text-gray-500">Total: {formatMontant(TARIFS.ecocontribution * nombreRuches)}</p>
                        ) : (
                          <p className="text-sm text-honey-600 mt-1">
                            ⚠️ SIRET et NAPI requis (complétez-les dans la section Assurance ci-dessus)
                          </p>
                        )}
                      </div>
                    </label>
                    <a
                      href="https://www.unaf-apiculture.info/nos-services/informations-reglementaires/etiquetage-ecocontribution/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 hover:text-indigo-700 text-sm flex items-center"
                    >
                      En savoir plus
                      <ArrowTopRightOnSquareIcon className="h-4 w-4 ml-1" />
                    </a>
                  </div>
                </div>
              ) : (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-300">
                  <div className="flex items-center text-gray-700">
                    <CheckCircleIcon className="h-5 w-5 mr-2 text-green-500" />
                    <span className="font-medium">Écocontribution déjà souscrite</span>
                  </div>
                </div>
              )}

            </div>

            {/* Récapitulatif */}
            <div className="bg-indigo-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-indigo-900 mb-2">Supplément à payer</h3>
              <p className="text-3xl font-bold text-indigo-600">{formatMontant(montantSupplementaire)}</p>
            </div>

            {/* Avertissement augmentation du nombre de ruches */}
            <div className="bg-honey-50 border-l-4 border-honey-400 p-4 rounded-r-lg">
              <div className="flex">
                <InformationCircleIcon className="h-5 w-5 text-honey-400 flex-shrink-0 mt-0.5" />
                <p className="ml-3 text-sm text-honey-700">
                  Une augmentation du nombre de ruches en cours d'année peut être prise en compte. Cette demande s'applique alors à tous les services, générant plusieurs compléments de paiement, y compris pour le montant de la cotisation de l'adhésion au SAR.
                </p>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setStep(2)}
                disabled={montantSupplementaire <= 0}
                className="px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Continuer vers la signature
              </button>
            </div>
          </div>
        )}

        {/* Étape 2: Signature */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-soft p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Récapitulatif des modifications</h2>
              <div className="space-y-2 text-sm mb-6">
                {modifications.assuranceFormule && (
                  <div className="flex justify-between">
                    <span>Nouvelle formule</span>
                    <span className="font-medium">{getFormuleLabel(modifications.assuranceFormule)}</span>
                  </div>
                )}
                {modifications.revue && (
                  <div className="flex justify-between">
                    <span>Revue ajoutée</span>
                    <span className="font-medium">{getRevueLabel(modifications.revue)}</span>
                  </div>
                )}
                {modifications.affairesJuridiques && (
                  <div className="flex justify-between">
                    <span>Affaires juridiques</span>
                    <span className="font-medium">Ajouté</span>
                  </div>
                )}
                {modifications.ecocontribution && (
                  <div className="flex justify-between">
                    <span>Écocontribution</span>
                    <span className="font-medium">Ajouté</span>
                  </div>
                )}
                <hr className="my-3" />
                <div className="flex justify-between text-lg font-bold">
                  <span>Total supplément</span>
                  <span className="text-indigo-600">{formatMontant(montantSupplementaire)}</span>
                </div>
              </div>

              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Signature numérique</h3>
                <p className="text-sm text-slate-600 mb-4">
                  En signant, vous acceptez les modifications ci-dessus et vous engagez à payer le supplément indiqué.
                </p>
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-2 bg-white">
                  <SignatureCanvas
                    ref={signatureRef}
                    canvasProps={{
                      className: 'w-full h-40 bg-white rounded',
                    }}
                    onEnd={handleSignature}
                  />
                </div>
                <div className="flex justify-end mt-2">
                  <button
                    type="button"
                    onClick={clearSignature}
                    className="text-sm text-slate-600 hover:text-slate-800"
                  >
                    Effacer la signature
                  </button>
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <button
                onClick={() => setStep(1)}
                className="px-6 py-3 bg-white border border-slate-300 text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition-colors"
              >
                Retour
              </button>
              <button
                onClick={handleSubmit}
                disabled={!signature || submitting}
                className="px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? 'Traitement...' : `Payer ${formatMontant(montantSupplementaire)}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
